import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from 'react';
import {
  createBrowserRouter,
  Outlet,
  ScrollRestoration,
  useLocation,
  useNavigate,
  useRouteError,
} from 'react-router';

import { BackBar } from '@/components/BackBar';
import { SwipeBack } from '@/components/SwipeBack';
import { ErrorState, Screen, Spinner } from '@/components/ui';
import { backFallback, hasInAppHistory } from '@/lib/backTarget';
import { texts } from '@/lib/texts';
import { trpc } from '@/lib/trpc';
import { HomeScreen } from '@/routes/HomeScreen';
import { NotFoundScreen } from '@/routes/NotFoundScreen';
import { AboutScreen } from '@/routes/onboarding/AboutScreen';
import { ActivitiesScreen } from '@/routes/onboarding/ActivitiesScreen';
import { CityScreen } from '@/routes/onboarding/CityScreen';
import { DistrictScreen } from '@/routes/onboarding/DistrictScreen';
import { ScheduleScreen } from '@/routes/onboarding/ScheduleScreen';
import { PoolScreen } from '@/routes/PoolScreen';

import { AppGate } from './AppGate';
import { useBackButton, useTma } from './TmaProvider';

/** Ссылка обратно в Mini App для страницы возврата из платёжной системы. */
const BOT_RETURN_URL = import.meta.env.VITE_BOT_URL ?? 'https://t.me/MeetUpMenBot';

/**
 * Первый экран (онбординг, пул, дом) лежит в основном чанке; остальные экраны грузятся
 * по маршруту (Stage 15, задача 4: бюджет 250 KB gz на первый экран).
 */
/**
 * Ключ, по которому отличаем «первый провал загрузки» от «перезагрузились и снова не вышло».
 * Без него сломанная сеть уводила бы страницу в цикл перезагрузок.
 */
const RELOAD_KEY = 'otryad.chunk-reload';

/**
 * Экран маршрута отдельным чанком. Имена файлов сборки содержат хеш, и после выкладки старые
 * пропадают: страница, открытая до релиза (Telegram держит Mini App в памяти), запрашивает
 * несуществующий чанк и падает белым экраном. Один раз перезагружаемся — этого достаточно,
 * чтобы взять свежий `index.html`; если и после перезагрузки не вышло, отдаём ошибку экрану.
 */
function screen(load: () => Promise<ComponentType>): LazyExoticComponent<ComponentType> {
  return lazy(() =>
    load()
      .then((component) => {
        sessionStorage.removeItem(RELOAD_KEY);
        return { default: component };
      })
      .catch((err: unknown) => {
        const reloaded = sessionStorage.getItem(RELOAD_KEY);
        if (!reloaded) {
          sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
          window.location.reload();
          // Возвращаем висящий промис: страница уже уходит на перезагрузку
          return new Promise<{ default: ComponentType }>(() => {});
        }
        throw err;
      }),
  );
}

const CaptainGatheringScreen = screen(() =>
  import('@/routes/CaptainGatheringScreen').then((m) => m.CaptainGatheringScreen),
);
const CityTableScreen = screen(() =>
  import('@/routes/CityTableScreen').then((m) => m.CityTableScreen),
);
const DevUiScreen = screen(() => import('@/routes/DevUiScreen').then((m) => m.DevUiScreen));
const FeedbackScreen = screen(() =>
  import('@/routes/FeedbackScreen').then((m) => m.FeedbackScreen),
);
const GatheringScreen = screen(() =>
  import('@/routes/GatheringScreen').then((m) => m.GatheringScreen),
);
const IdentityScreen = screen(() =>
  import('@/routes/IdentityScreen').then((m) => m.IdentityScreen),
);
const InviteScreen = screen(() => import('@/routes/InviteScreen').then((m) => m.InviteScreen));
// Stage 17, задача 7: «Мои сборы» и заявка — свои чанки (пергамент и антиква не на первом экране)
const MyGatheringsScreen = screen(() =>
  import('@/routes/MyGatheringsScreen').then((m) => m.MyGatheringsScreen),
);
const BookingScreen = screen(() => import('@/routes/BookingScreen').then((m) => m.BookingScreen));const OpenSeatsScreen = screen(() =>
  import('@/routes/OpenSeatsScreen').then((m) => m.OpenSeatsScreen),
);
const PayResultScreen = screen(() =>
  import('@/routes/PayResultScreen').then((m) => m.PayResultScreen),
);
const PaySeasonScreen = screen(() =>
  import('@/routes/PaySeasonScreen').then((m) => m.PaySeasonScreen),
);
const PaymentsScreen = screen(() =>
  import('@/routes/PaymentsScreen').then((m) => m.PaymentsScreen),
);
const PoolCellScreen = screen(() =>
  import('@/routes/PoolCellScreen').then((m) => m.PoolCellScreen),
);
const OpenCellScreen = screen(() =>
  import('@/routes/OpenCellScreen').then((m) => m.OpenCellScreen),
);
const PrivacyScreen = screen(() =>
  import('@/routes/PrivacyScreen').then((m) => m.PrivacyScreen),
);
const SeatScreen = screen(() => import('@/routes/SeatScreen').then((m) => m.SeatScreen));
const PaySuccessScreen = screen(() =>
  import('@/routes/PayReturnScreen').then((m) => () => (
    <m.PayReturnScreen ok botUrl={BOT_RETURN_URL} />
  )),
);
const PayFailScreen = screen(() =>
  import('@/routes/PayReturnScreen').then((m) => () => (
    <m.PayReturnScreen ok={false} botUrl={BOT_RETURN_URL} />
  )),
);
const ProfileScreen = screen(() => import('@/routes/ProfileScreen').then((m) => m.ProfileScreen));
const ProposeScreen = screen(() => import('@/routes/ProposeScreen').then((m) => m.ProposeScreen));
const ReserveScreen = screen(() => import('@/routes/ReserveScreen').then((m) => m.ReserveScreen));
const SeasonScreen = screen(() => import('@/routes/SeasonScreen').then((m) => m.SeasonScreen));
const SquadScreen = screen(() => import('@/routes/SquadScreen').then((m) => m.SquadScreen));

/** Корневые экраны (вкладки): на них нативная кнопка «Назад» не показывается. */
const ROOT_PATHS = new Set([
  '/',
  '/home',
  '/squad',
  '/season',
  '/pool',
  '/my',
  '/onboarding/city',
  '/dev/ui',
]);
const PROFILE_PATH = '/profile';

function LazyFallback() {
  return (
    <Screen>
      <div className="flex flex-1 items-center justify-center py-24">
        <Spinner />
      </div>
    </Screen>
  );
}

function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const tma = useTma();
  // Профиль участника отряда — вкладка нижней панели, а у вкладки нет «Назад» (раунд 3, M9).
  // Человеку в пуле панели нет, и в профиль он приходит с экрана набора — там «Назад» нужна.
  const me = trpc.auth.me.useQuery(undefined, { enabled: location.pathname === PROFILE_PATH });
  // С «Моими сборами» (Stage 17) панель есть и у человека в пуле — там профиль тоже вкладка
  const profileIsTab =
    location.pathname === PROFILE_PATH &&
    (me.data?.state === 'inSquad' ||
      (me.data?.state === 'inPool' && me.data.features.myGatherings));
  const nested = !ROOT_PATHS.has(location.pathname) && !profileIsTab;
  // Нет предыдущей записи (открыли по ссылке, вернулись в анкету) — не уходим из приложения
  const goBack = () =>
    void (hasInAppHistory(window.history.state)
      ? navigate(-1)
      : navigate(backFallback(location.pathname), { replace: true }));
  useBackButton(nested, goBack);
  // Клиента Telegram нет — рисовать «Назад» некому, и вложенный экран становился тупиком
  // («Мои оплаты» без выхода). Внутри Telegram кнопка своя, вторую не показываем.
  const needsOwnBack = nested && (tma.mocked || !tma.available);
  return (
    <SwipeBack enabled={nested} onBack={goBack}>
      {/* Новый экран открывается сверху, «Назад» возвращает прежнюю прокрутку (ревью 2026-09-24) */}
      <ScrollRestoration />
      <Suspense fallback={<LazyFallback />}>
        {needsOwnBack ? <BackBar onBack={goBack} /> : null}
        <Outlet />
      </Suspense>
    </SwipeBack>
  );
}

function RouteErrorScreen() {
  const error = useRouteError();
  if (import.meta.env.DEV) console.error(error);
  return (
    <Screen>
      <ErrorState
        title={texts.common.errorTitle}
        body={texts.common.errorBody}
        onRetry={() => {
          // Полная перезагрузка, а не переход по маршруту: чаще всего экран сюда попадает из-за
          // устаревшей страницы после выкладки, и новый маршрут упрётся в тот же мёртвый чанк.
          sessionStorage.removeItem(RELOAD_KEY);
          window.location.href = '/';
        }}
        retryLabel={texts.common.toHome}
      />
    </Screen>
  );
}

export const router = createBrowserRouter([
  {
    element: <AppShell />,
    errorElement: <RouteErrorScreen />,
    children: [
      { path: '/', element: <AppGate /> },

      { path: '/onboarding/city', element: <CityScreen /> },
      { path: '/onboarding/district', element: <DistrictScreen /> },
      { path: '/onboarding/schedule', element: <ScheduleScreen /> },
      { path: '/onboarding/activities', element: <ActivitiesScreen /> },
      { path: '/onboarding/about', element: <AboutScreen /> },

      { path: '/pool', element: <PoolScreen /> },
      { path: '/pool/propose', element: <ProposeScreen /> },
      { path: '/pool/cell/:applicationId', element: <PoolCellScreen /> },
      { path: '/pool/open/:cellKey', element: <OpenCellScreen /> },
      { path: '/privacy', element: <PrivacyScreen /> },
      { path: '/open-seats/:key', element: <SeatScreen /> },
      // Возврат из платёжной системы обычно приходит во внешний браузер (там страницу отдаёт
      // `App` до гейта), но открытый внутри Telegram адрес не должен упираться в «не найдено».
      { path: '/pay/success', element: <PaySuccessScreen /> },
      { path: '/pay/fail', element: <PayFailScreen /> },
      { path: '/profile', element: <ProfileScreen /> },

      { path: '/home', element: <HomeScreen /> },
      { path: '/squad', element: <SquadScreen /> },
      { path: '/squad/reserve', element: <ReserveScreen /> },
      { path: '/squad/identity', element: <IdentityScreen /> },
      { path: '/city', element: <CityTableScreen /> },
      { path: '/season', element: <SeasonScreen /> },
      { path: '/gathering/:id', element: <GatheringScreen /> },
      { path: '/gathering/:id/feedback', element: <FeedbackScreen /> },

      { path: '/payments', element: <PaymentsScreen /> },
      { path: '/pay/season/:seasonId', element: <PaySeasonScreen /> },
      { path: '/pay/result/:orderId', element: <PayResultScreen /> },

      { path: '/open-seats', element: <OpenSeatsScreen /> },
      { path: '/my', element: <MyGatheringsScreen /> },
      { path: '/booking/:id', element: <BookingScreen /> },
      { path: '/invite', element: <InviteScreen /> },
      { path: '/captain/gathering/:id', element: <CaptainGatheringScreen /> },

      ...(import.meta.env.DEV ? [{ path: '/dev/ui', element: <DevUiScreen /> }] : []),

      { path: '*', element: <NotFoundScreen /> },
    ],
  },
]);
